import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, Form, Select, InputNumber, Tabs, Descriptions, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, PlusCircleOutlined, SearchOutlined, FilterOutlined, ReloadOutlined, EyeOutlined, FileExcelOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'underscore';
import TextArea from 'antd/es/input/TextArea';

import { AppDispatch, CombinedStates } from '../../core/types';
import { Project } from '../../core/projects/types';
import { getSchoolProjects, createOrUpdateProject, getProjectCategories, exportProjects, deleteProject } from '../../core/projects/projectsApi';
import { loadSingleProject, resetSingleProject } from '../../core/projects/projectsSlice';

import './style.scss';

const { Option } = Select;
const { TabPane } = Tabs;

interface SchoolProjectsProps {
    schoolId: number;
}

const SchoolProjects: React.FC<SchoolProjectsProps> = ({ schoolId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [viewProject, setViewProject] = useState<Project | null>(null);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(undefined);
    const [form] = Form.useForm();

    const {
        projects,
        isFetching,
        singleProject,
        categories,
        isCategoriesFetching,
        isExporting,
    } = useSelector((state: CombinedStates) => state.projects);

    useEffect(() => {
        dispatch(getProjectCategories());
        dispatch(getSchoolProjects({ school_id: schoolId }));
    }, [dispatch, schoolId]);

    // Local filtering logic
    const filteredProjects = useMemo(() => {
        let filtered = [...projects];

        // Filter by search term
        if (searchTerm.trim()) {
            filtered = filtered.filter(project => project.title.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        // Filter by category
        if (selectedCategoryIds.length > 0) {
            filtered = filtered.filter(project => selectedCategoryIds.includes(project.project_category_id));
        }

        // Filter by period
        if (selectedPeriod) {
            filtered = filtered.filter(project => project.period === selectedPeriod);
        }

        return filtered;
    }, [projects, searchTerm, selectedCategoryIds, selectedPeriod]);

    // Get unique periods from projects
    const availablePeriods = useMemo(() => {
        const periods = projects.map(p => p.period).filter((p, i, arr) => arr.indexOf(p) === i);
        return periods.sort().reverse();
    }, [projects]);

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                const payload = {
                    ...values,
                    school_id: schoolId,
                    id: singleProject?.id || undefined,
                };
                dispatch(createOrUpdateProject(payload));
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleProject());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleProject(id));
        setIsModalVisible(true);
    };

    const handleView = (project: Project) => {
        setViewProject(project);
        setIsViewModalVisible(true);
    };

    const handleDelete = (project: Project) => {
        setProjectToDelete(project);
        setIsDeleteModalVisible(true);
    };

    const handleDeleteConfirm = () => {
        if (projectToDelete?.id) {
            dispatch(deleteProject({ id: projectToDelete.id }));
            setIsDeleteModalVisible(false);
            setProjectToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
        setProjectToDelete(null);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleProject());
    };

    const handleFilterCancel = () => {
        setIsFilterModalVisible(false);
    };

    const handleFilterApply = () => {
        setIsFilterModalVisible(false);
    };

    const handleFilterReset = () => {
        setSelectedCategoryIds([]);
        setSelectedPeriod(undefined);
    };

    const handleExport = async () => {
        try {
            const payload: {
                school_id: number;
                period?: string;
                project_category_id?: number;
            } = {
                school_id: schoolId,
            };

            // Add filters if selected
            if (selectedPeriod) {
                payload.period = selectedPeriod;
            }

            // If multiple categories selected, we'll need to handle it differently
            // For now, if only one category is selected, use it
            if (selectedCategoryIds.length === 1) {
                const [categoryId] = selectedCategoryIds;
                if (categoryId) {
                    payload.project_category_id = categoryId;
                }
            }

            const response = await dispatch(exportProjects(payload)).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `projektai_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            notification.success({
                message: 'Pavyko!',
                description: 'Projektai sėkmingai eksportuoti',
            });
        } catch (error) {
            console.error('Export failed:', error);
            notification.error({
                message: 'Klaida',
                description: 'Nepavyko eksportuoti projektų',
            });
        }
    };

    useEffect(() => {
        if (singleProject && isModalVisible) {
            // Convert budget_eur from string to number for form
            const formValues = {
                ...singleProject,
                budget_eur: singleProject.budget_eur ? parseFloat(singleProject.budget_eur) : null,
            };
            form.setFieldsValue(formValues);
        }
    }, [singleProject, isModalVisible, form]);

    // Debounced search handler
    const debouncedSearch = debounce((value: string) => {
        setSearchTerm(value);
    }, 300);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        debouncedSearch(value);
    };

    const columns: ColumnsType<Project> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a: Project, b: Project) => a.id - b.id,
        },
        {
            title: 'Pavadinimas',
            dataIndex: 'title',
            key: 'title',
            sorter: (a: Project, b: Project) => a.title.localeCompare(b.title),
        },
        {
            title: 'Kategorija',
            dataIndex: 'project_category_id',
            key: 'project_category_id',
            render: (categoryId: number, { category }: Project) => (
                category ? <Tag color="blue">{category.title}</Tag> : categoryId
            ),
        },
        {
            title: 'Projekto numeris',
            dataIndex: 'project_number',
            key: 'project_number',
            render: (text: string | null) => text || '-',
        },
        {
            title: 'Laikotarpis',
            dataIndex: 'period_description',
            key: 'period_description',
            sorter: (a: Project, b: Project) => a.period.localeCompare(b.period),
        },
        {
            title: 'Koordinavimo institucija',
            dataIndex: 'coordinating_institution',
            key: 'coordinating_institution',
            render: (text: string | null) => text || '-',
        },
        {
            title: 'Biudžetas (EUR)',
            dataIndex: 'budget_eur',
            key: 'budget_eur',
            render: (text: string | null) => {
                if (!text) return '-';
                return parseFloat(text).toLocaleString('lt-LT', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            },
            sorter: (a: Project, b: Project) => {
                const aVal = a.budget_eur ? parseFloat(a.budget_eur) : 0;
                const bVal = b.budget_eur ? parseFloat(b.budget_eur) : 0;
                return aVal - bVal;
            },
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (text: string, record: Project) => (
                <Space size="middle">
                    <Tooltip title="Peržiūrėti projektą">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(record)} />
                    </Tooltip>
                    <Tooltip title="Redaguoti projektą">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => record.id && handleEdit(record.id)} />
                    </Tooltip>
                    <Tooltip title="Ištrinti projektą">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const activeCategories = useMemo(() => categories.filter(c => c.is_active).sort((a, b) => a.order - b.order), [categories]);

    return (
        <div className="school-projects">
            <Tabs defaultActiveKey="data-entry">
                <TabPane tab="Duomenų suvedimas" key="data-entry">
                    <Card>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col>
                                <Input
                                    placeholder="Ieškoti projektų..."
                                    prefix={<SearchOutlined />}
                                    onChange={handleSearch}
                                    style={{ width: 300 }} />
                            </Col>
                            <Col>
                                <Space>
                                    <Button
                                        icon={<FilterOutlined />}
                                        onClick={() => setIsFilterModalVisible(true)}
                                        type={selectedCategoryIds.length > 0 || selectedPeriod ? 'primary' : 'default'}>
                                        Filtrai
                                        {(selectedCategoryIds.length > 0 || selectedPeriod)
                                            && ` (${selectedCategoryIds.length + (selectedPeriod ? 1 : 0)})`}
                                    </Button>
                                    <Tooltip title="Perkrauti sąrašą">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={() => dispatch(getSchoolProjects({ school_id: schoolId }))}
                                            loading={isFetching} />
                                    </Tooltip>
                                    <Tooltip title="Eksportuoti į Excel">
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={handleExport}
                                            loading={isExporting} />
                                    </Tooltip>
                                    <Button
                                        type="primary"
                                        icon={<PlusCircleOutlined />}
                                        onClick={() => setIsModalVisible(true)}>
                                        Pridėti projektą
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Table
                            columns={columns}
                            dataSource={filteredProjects}
                            rowKey="id"
                            loading={isFetching}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} projektų`,
                            }} />
                    </Card>
                </TabPane>
            </Tabs>

            {/* Add/Edit Project Modal */}
            <Modal
                title={singleProject?.id ? 'Redaguoti projektą' : 'Pridėti naują projektą'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={singleProject?.id ? 'Atnaujinti' : 'Pridėti'}
                cancelText="Atšaukti"
                width={800}
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                <Form layout="vertical" form={form}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Pavadinimas"
                                name="title"
                                rules={[
                                    { required: true, message: 'Įveskite projekto pavadinimą' },
                                    { max: 255, message: 'Pavadinimas negali viršyti 255 simbolių' },
                                ]}>
                                <Input placeholder="Projekto pavadinimas" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Kategorija"
                                name="project_category_id"
                                rules={[
                                    { required: true, message: 'Pasirinkite projekto kategoriją' },
                                ]}>
                                <Select
                                    placeholder="Pasirinkite kategoriją"
                                    loading={isCategoriesFetching}>
                                    {activeCategories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.title}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Projekto numeris"
                                name="project_number">
                                <Input placeholder="Projekto numeris" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Biudžetas (EUR)"
                                name="budget_eur"
                                rules={[
                                    { type: 'number', min: 0, message: 'Biudžetas turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0.00"
                                    min={0}
                                    step={0.01}
                                    style={{ width: '100%' }}
                                    precision={2} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Koordinavimo institucija"
                                name="coordinating_institution">
                                <Input placeholder="Koordinavimo institucija" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Partneriai"
                                name="partners">
                                <TextArea
                                    placeholder="Partneriai"
                                    rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Įgyvendinimo periodas"
                                name="implementation_period">
                                <Input placeholder="Pvz.: 2024-01-01 - 2025-12-31" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Finansavimo organizacija"
                                name="funding_organization">
                                <Input placeholder="Finansavimo organizacija" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Tikslas"
                                name="goal">
                                <TextArea
                                    placeholder="Projekto tikslas"
                                    rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Rezultatai"
                                name="results">
                                <TextArea
                                    placeholder="Projekto rezultatai"
                                    rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Pastabos"
                                name="notes">
                                <TextArea
                                    placeholder="Papildomos pastabos"
                                    rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* View Project Modal */}
            <Modal
                title="Projekto peržiūra"
                visible={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Uždaryti
                    </Button>,
                ]}
                width={800}
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {viewProject && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="ID">{viewProject.id}</Descriptions.Item>
                        <Descriptions.Item label="Pavadinimas">{viewProject.title}</Descriptions.Item>
                        <Descriptions.Item label="Kategorija">
                            {viewProject.category ? viewProject.category.title : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Laikotarpis">{viewProject.period_description}</Descriptions.Item>
                        <Descriptions.Item label="Projekto numeris">{viewProject.project_number || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Koordinavimo institucija">
                            {viewProject.coordinating_institution || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Partneriai">
                            {viewProject.partners || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Įgyvendinimo periodas">
                            {viewProject.implementation_period || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Finansavimo organizacija">
                            {viewProject.funding_organization || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Biudžetas (EUR)">
                            {viewProject.budget_eur
                                ? parseFloat(viewProject.budget_eur).toLocaleString('lt-LT', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })
                                : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Tikslas">
                            {viewProject.goal || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Rezultatai">
                            {viewProject.results || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pastabos">
                            {viewProject.notes || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Sukurta">
                            {viewProject.created_at}
                        </Descriptions.Item>
                        <Descriptions.Item label="Atnaujinta">
                            {viewProject.updated_at}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Filter Modal */}
            <Modal
                title="Filtrai"
                visible={isFilterModalVisible}
                onOk={handleFilterApply}
                onCancel={handleFilterCancel}
                okText="Taikyti"
                cancelText="Atšaukti"
                width={400}>
                <div style={{ marginBottom: 16 }}>
                    <h4>Kategorija</h4>
                    <Select
                        mode="multiple"
                        placeholder="Pasirinkite kategorijas"
                        value={selectedCategoryIds}
                        onChange={setSelectedCategoryIds}
                        style={{ width: '100%' }}
                        allowClear
                        maxTagCount="responsive"
                        loading={isCategoriesFetching}>
                        {activeCategories.map(category => (
                            <Option key={category.id} value={category.id}>
                                {category.title}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <h4>Laikotarpis</h4>
                    <Select
                        placeholder="Pasirinkite laikotarpį"
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}
                        style={{ width: '100%' }}
                        allowClear>
                        {availablePeriods.map(period => (
                            <Option key={period} value={period}>
                                {period}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleFilterReset} style={{ marginRight: 8 }}>
                        Išvalyti
                    </Button>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Ištrinti projektą"
                visible={isDeleteModalVisible}
                onOk={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                okText="Taip, ištrinti"
                cancelText="Atšaukti"
                okButtonProps={{ danger: true }}
                width={500}>
                <p>Ar tikrai norite ištrinti šį projektą?</p>
                {projectToDelete && (
                    <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <strong>Projekto pavadinimas:</strong> {projectToDelete.title}
                    </div>
                )}
                <p style={{ marginTop: 16, color: '#ff4d4f' }}>
                    <strong>Dėmesio:</strong> Šis veiksmas negrįžtamas.
                </p>
            </Modal>
        </div>
    );
};

export default SchoolProjects;
